import React, { useState, useEffect, useRef } from "react";
import { NavLink } from "react-router-dom";
import { getAccountInfo } from "../api/rest-apis";
import posthog from "posthog-js";

interface UserInfo {
  firstName: string;
  lastName: string;
  email: string;
}

const SideMenu: React.FC = () => {
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem("sidenavCollapsed");
    return saved ? JSON.parse(saved) : true;
  });

  const [userInfo, setUserInfo] = useState<UserInfo>(() => {
    // Load cached user info from localStorage on initial render
    const cached = localStorage.getItem("cachedUserInfo");
    if (cached) {
      try {
        return JSON.parse(cached);
      } catch {
        return { firstName: "", lastName: "", email: "" };
      }
    }
    return { firstName: "", lastName: "", email: "" };
  });

  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };

    if (isUserMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isUserMenuOpen]);

  useEffect(() => {
    // Only fetch if we don't have cached data or if user is logged in
    if (localStorage.getItem("token")) {
      fetchUserInfo();
    }
  }, []);

  useEffect(() => {
    localStorage.setItem("sidenavCollapsed", JSON.stringify(isCollapsed));
    // Update body class for main content margin adjustment
    if (isCollapsed) {
      document.body.classList.remove("sidenav-expanded");
      document.body.classList.add("sidenav-collapsed");
    } else {
      document.body.classList.remove("sidenav-collapsed");
      document.body.classList.add("sidenav-expanded");
    }
  }, [isCollapsed]);

  // Set initial body class on mount
  useEffect(() => {
    if (isCollapsed) {
      document.body.classList.add("sidenav-collapsed");
    } else {
      document.body.classList.add("sidenav-expanded");
    }
    return () => {
      document.body.classList.remove("sidenav-collapsed", "sidenav-expanded");
    };
  }, []);

  const fetchUserInfo = async () => {
    try {
      const response: any = await getAccountInfo();
      const newUserInfo = {
        firstName:
          response.firstName.charAt(0).toUpperCase() +
          response.firstName.slice(1),
        lastName: response.lastName
          ? response.lastName.charAt(0).toUpperCase() +
            response.lastName.slice(1)
          : "",
        email: response.email,
      };
      setUserInfo(newUserInfo);
      // Cache user info in localStorage
      localStorage.setItem("cachedUserInfo", JSON.stringify(newUserInfo));
    } catch (error) {
      console.error(error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("user_id");
    localStorage.removeItem("email");
    localStorage.removeItem("cachedUserInfo");
    posthog.reset();
    document.cookie = `logged_in=; path=/; domain=bookishboyfriend.com; expires=Thu, 01 Jan 1970 00:00:00 UTC; Secure; SameSite=None`;
    window.location.href = `${process.env.REACT_APP_REST_API_URI}/logout`;
  };

  const toggleSidenav = () => {
    setIsCollapsed(!isCollapsed);
  };

  const getInitial = () => {
    return userInfo.firstName ? userInfo.firstName.charAt(0).toUpperCase() : "?";
  };

  const getFullName = () => {
    return `${userInfo.firstName} ${userInfo.lastName}`.trim();
  };

  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    isActive ? "sidenav-item nav-active" : "sidenav-item";

  const navItems = [
    { path: "/", icon: "home.svg", label: "Home" },
    { path: "/chats", icon: "chats.svg", label: "Chats" },
    { path: "/saved", icon: "heart.svg", label: "Saved" },
    { path: "/price", icon: "bill.svg", label: "Pricing" },
    { path: "https://community.bookishboyfriend.com/", icon: "community.svg", label: "Community", external: true },
    { path: "/support", icon: "support-white.svg", label: "Support" },
  ];

  const toggleUserMenu = () => {
    setIsUserMenuOpen(!isUserMenuOpen);
  };

  return (
    <div className={`side-nav ${!isCollapsed ? "expanded" : ""}`}>
      {/* Toggle Button */}
      <button className="sidenav-toggle" onClick={toggleSidenav}>
        <svg
          viewBox="0 0 24 24"
          fill="none"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="9 18 15 12 9 6" />
        </svg>
      </button>

      {/* Main Navigation */}
      <nav className="main-nav">
        {navItems.map((item) =>
          item.external ? (
            <a
              key={item.path}
              href={item.path}
              className="sidenav-item"
              data-tooltip={item.label}
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                src={`/assets/images/${item.icon}`}
                alt={item.label}
                className="sidenav-icon"
              />
              <span className="sidenav-label">{item.label}</span>
            </a>
          ) : (
            <NavLink
              key={item.path}
              to={item.path}
              className={navLinkClass}
              data-tooltip={item.label}
              end={item.path === "/"}
            >
              <img
                src={`/assets/images/${item.icon}`}
                alt={item.label}
                className="sidenav-icon"
              />
              <span className="sidenav-label">{item.label}</span>
            </NavLink>
          )
        )}
      </nav>

      {/* Bottom Navigation - User Menu */}
      <div className="nav-bottom">
        <div className="sidenav-user-menu-container" ref={userMenuRef}>
          {/* Upward Dropdown */}
          {isUserMenuOpen && (
            <div className="sidenav-user-dropdown">
              {/* Settings Link */}
              <NavLink
                to="/setting"
                className="sidenav-dropdown-item"
                onClick={() => setIsUserMenuOpen(false)}
              >
                <img
                  src="/assets/images/setting.svg"
                  alt="Settings"
                  className="sidenav-dropdown-icon"
                />
                <span>Settings</span>
              </NavLink>

              {/* Logout Button */}
              {localStorage.getItem("token") && (
                <button
                  className="sidenav-dropdown-item sidenav-dropdown-logout"
                  onClick={handleLogout}
                >
                  <img
                    src="/assets/images/logout.svg"
                    alt="Logout"
                    className="sidenav-dropdown-icon"
                  />
                  <span>Logout</span>
                </button>
              )}
            </div>
          )}

          {/* User Card Button */}
          <button
            className="sidenav-user-card"
            onClick={toggleUserMenu}
            data-tooltip={localStorage.getItem("token") ? getFullName() || "Menu" : "Menu"}
          >
            {/* Avatar - always visible */}
            <div className="sidenav-user-avatar">
              <span>{getInitial()}</span>
            </div>

            {/* User details - only when expanded */}
            <div className="sidenav-user-details">
              <p className="sidenav-user-name">{getFullName()}</p>
              <p className="sidenav-user-email">{userInfo.email}</p>
            </div>

            {/* Chevron - only when expanded */}
            <svg
              className={`sidenav-user-chevron ${isUserMenuOpen ? "open" : ""}`}
              viewBox="0 0 24 24"
              fill="none"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="18 15 12 9 6 15" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default SideMenu;
